import { useCallback } from 'react';

import { useFragment as useApolloFragment } from '@apollo/client/react';
import { Box, Skeleton, Stack } from '@mui/material';
import { generatedTextToEditorHtml } from '@realadvisor/html-sanitizer/generated-text';

import type { StepDescriptionBrochureFragment } from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import { LotGenerateDescription } from '@modules/_shared/components/LotGenerateDescription';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { isNullOrEmpty } from '@modules/_shared/utils/strings';
import { STEP_DESCRIPTION_BROCHURE_FRAGMENT } from '@modules/reports/brochure-report/brochureReportsQueries';
import {
  DescriptionLanguageTabs,
  LANGUAGE_API_NAMES,
} from '@modules/reports/shared/DescriptionLanguageTabs';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import {
  getBrokerLanguage,
  useLanguageTranslations,
  useTranslationUpdate,
} from '@modules/reports/shared/language';
import { useUpdateListingBrochure } from '@modules/reports/shared/utils';

type StepDescriptionForm = {
  use_custom_brochure_description: boolean;
  brochure_description_layout: number;
};

const StepDescriptionBrochure = (props: ReportComponentProps) => {
  const { reportId: listingId } = props;
  const { descriptionLanguage, setError, setDescriptionLanguage } =
    useReportContext();

  const { complete, data } = useApolloFragment<StepDescriptionBrochureFragment>(
    {
      fragment: STEP_DESCRIPTION_BROCHURE_FRAGMENT,
      fragmentName: 'StepDescriptionBrochure',
      from: {
        __typename: 'lots',
        id: listingId,
      },
    },
  );

  const [updateListingBrochure, updating] = useUpdateListingBrochure(
    listingId,
    'page-description',
  );

  const { updateTranslation, debouncedTranslationUpdate } =
    useTranslationUpdate(updateListingBrochure, 'brochure_description');

  const onChangeHandler: RaFormOnChange<StepDescriptionForm> = useCallback(
    (formData, _name, type) => {
      if (type !== 'change') {
        return;
      }

      const mutation: Record<string, unknown> = { ...formData };

      // Seed brochure description from listing description when toggling, only if empty
      const brokerLang = getBrokerLanguage(data?.broker);
      const brochureDescKey = `brochure_description_${brokerLang}`;
      const currentBrochureDesc = data?.[brochureDescKey as keyof typeof data];
      if (isNullOrEmpty(currentBrochureDesc, true)) {
        mutation[brochureDescKey] = data?.description ?? null;
      }

      updateListingBrochure(mutation);
    },
    [updateListingBrochure, data],
  );

  const brokerLang = getBrokerLanguage(data?.broker);
  const translations = useLanguageTranslations(data, 'brochure_description', {
    fallbackValue: data?.description ?? null,
    fallbackLanguage: brokerLang,
  });

  const formDefinition: FormDefinitionType<StepDescriptionForm> = useCallback(
    ({ t }) => [
      {
        name: 'brochure_description_layout',
        label: t('Columns'),
        type: 'select',
        gridProps: { md: 12 },
        options: () => [
          { label: '1', value: 1 },
          { label: '2', value: 2 },
        ],
      },
      {
        name: 'use_custom_brochure_description',
        label: t('Use custom listing description'),
        type: 'checkbox',
        gridProps: { md: 12 },
        style: 'switch',
      },
      {
        name: 'description_language_tabs',
        type: 'custom',
        gridProps: { md: 12 },
        element: (
          <DescriptionLanguageTabs
            defaultLanguage={descriptionLanguage}
            translations={translations}
            onTranslationChange={debouncedTranslationUpdate}
            onTranslationComplete={updateTranslation}
            onTranslationDelete={lang =>
              updateListingBrochure({ [`brochure_description_${lang}`]: null })
            }
            onDescriptionLanguageChange={setDescriptionLanguage}
            disabled={!data?.use_custom_brochure_description}
            actions={(
              activeTab,
              setContent,
              translateFrom,
              sourceLanguages,
            ) => (
              <LotGenerateDescription
                lot_id={listingId}
                description={translations[activeTab] ?? ''}
                language={LANGUAGE_API_NAMES[activeTab]}
                onComplete={(text: string) => {
                  const html = generatedTextToEditorHtml(text);
                  updateTranslation(activeTab, html);
                  setContent(html);
                }}
                onError={setError}
                translateFrom={translateFrom}
                sourceLanguages={sourceLanguages.map(code => ({
                  code,
                  name: LANGUAGE_API_NAMES[code],
                }))}
                hideExpertMode={true}
              />
            )}
          />
        ),
      },
    ],
    [
      descriptionLanguage,
      translations,
      debouncedTranslationUpdate,
      updateTranslation,
      updateListingBrochure,
      setDescriptionLanguage,
      data?.use_custom_brochure_description,
      listingId,
      setError,
    ],
  );

  const onSubmit = async (formData: StepDescriptionForm) => {
    await updateListingBrochure(formData);
    props.setStep(props.step + 1);
  };

  if (!complete) {
    return (
      <Box sx={{ flexGrow: 1, position: 'relative', overflowY: 'auto', p: 2 }}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Stack
              key={`step-description-skeleton-${index}`}
              sx={{
                gap: 1,
              }}
            >
              <Skeleton variant="rounded" height={20} width="30%" />
              <Skeleton variant="rounded" height={40} />
            </Stack>
          ))}
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </Box>
    );
  }

  return (
    <RaForm
      freezeInitialDefaultValues={true}
      formDefinition={formDefinition}
      defaultValues={{
        use_custom_brochure_description: data?.use_custom_brochure_description,
        brochure_description_layout: data?.brochure_description_layout,
      }}
      onSubmit={onSubmit}
      onChange={onChangeHandler}
      contentScrollable
      actionButtonsComponent={<FooterActions {...props} updating={updating} />}
    />
  );
};

export default StepDescriptionBrochure;
